import { AgChartOptions, AllCommunityModule, ModuleRegistry } from 'ag-charts-community';
import { AgCharts } from 'ag-charts-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../styles/CPDashboard.css';

// Register AG Charts community modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Location entity from Azure Table
interface LocationEntity {
    partitionKey: string;
    rowKey: string;
    server: string;
    browser_session: string;
    user_id: string;
    starting_post_code: string;
    miles_radius: number;
    residential_needs: boolean;
    dementia_needs: boolean;
    nursing_needs: boolean;
    sort: string;
    fundingType: string;
    search_position: number;
}

// Dashboard API response
interface DashboardResponse {
    success: boolean;
    requestedIds: { id: string; organisation_name: string }[];
    data: Record<string, LocationEntity[]>;
    meta: {
        queriedIds: number;
        totalRecords: number;
        queryDurationMs: number;
        periodMonths: number;
    };
}

// Chart data structures
interface MonthlySearchData {
    month: string;
    [careHomeId: string]: number | string;
}

interface StackedBarData {
    month: string;
    careHomeId: string;
    careHomeName: string;
    top5: number;
    remaining: number;
}

// Score card data structures
interface PeriodStats {
    current: number;
    previous: number;
    change: number;
    changePercent: number;
}

interface TotalAppearancesStats {
    last7Days: PeriodStats;
    last30Days: PeriodStats;
    last90Days: PeriodStats;
}

interface CareHomeRankingStats {
    id: string;
    name: string;
    avgPosition: number;
    totalAppearances: number;
    top5Count: number;
    top5Percent: number;
    color: string;
}

// Brand color palette for care homes (OpenScore guidelines)
const CHART_COLORS = [
    '#025A51', // Deep Teal
    '#028C7F', // Sea Green
    '#8ACDBA', // Aqua
    '#ED530F', // Highlight
    '#0B2E33', // Ink
    '#3A8F85', // Teal variant
    '#5BA89E', // Light teal
    '#D4725C', // Coral variant
    '#6B9E96', // Sage
    '#A85D3C'  // Rust
];

const API_BASE_URL = import.meta.env.PROD ? '' : 'http://localhost:3001';

const CPDashboard = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);

    // Get idcollection from URL params
    const idcollection = searchParams.get('idcollection');

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!idcollection) {
                setError('Missing idcollection parameter. Please provide care home IDs.');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`${API_BASE_URL}/api/dashboard_data?idcollection=${encodeURIComponent(idcollection)}`);
                
                if (!response.ok) {
                    const errorData = await response.json();
                    if (response.status === 404) {
                        setError(errorData.message || 'Invalid request');
                    } else if (response.status === 403) {
                        setError(`Invalid care home IDs: ${errorData.missingIds?.join(', ') || 'Unknown IDs'}`);
                    } else if (response.status === 503) {
                        setError('Dashboard data is temporarily unavailable');
                    } else {
                        setError('Failed to load dashboard data');
                    }
                    setLoading(false);
                    return;
                }

                const data: DashboardResponse = await response.json();
                setDashboardData(data);
                setLoading(false);
            } catch (err) {
                console.error('Dashboard fetch error:', err);
                setError('Failed to connect to the server');
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [idcollection]);

    // Helper function to check if date is within days ago
    const isWithinDays = (dateStr: string, days: number, fromDate: Date = new Date()): boolean => {
        const date = new Date(dateStr.substring(0, 23));
        const cutoff = new Date(fromDate);
        cutoff.setDate(cutoff.getDate() - days);
        return date >= cutoff && date <= fromDate;
    };

    // Process total appearances stats (7, 30, 90 days with comparisons)
    const totalAppearancesStats = useMemo((): TotalAppearancesStats | null => {
        if (!dashboardData) return null;

        const now = new Date();
        const allRecords = Object.values(dashboardData.data).flat();

        const calculatePeriodStats = (days: number): PeriodStats => {
            // Current period: last N days from now
            const currentCount = allRecords.filter(r => isWithinDays(r.rowKey, days, now)).length;
            
            // Previous period: N days before the current period
            const previousPeriodEnd = new Date(now);
            previousPeriodEnd.setDate(previousPeriodEnd.getDate() - days);
            const previousCount = allRecords.filter(r => isWithinDays(r.rowKey, days, previousPeriodEnd)).length;

            const change = currentCount - previousCount;
            const changePercent = previousCount > 0 ? ((change / previousCount) * 100) : (currentCount > 0 ? 100 : 0);

            return { current: currentCount, previous: previousCount, change, changePercent };
        };

        return {
            last7Days: calculatePeriodStats(7),
            last30Days: calculatePeriodStats(30),
            last90Days: calculatePeriodStats(90),
        };
    }, [dashboardData]);

    // Process average ranking stats for each care home (last 30 days)
    const careHomeRankingStats = useMemo((): CareHomeRankingStats[] => {
        if (!dashboardData) return [];

        const now = new Date();
        const careHomeNames = new Map(dashboardData.requestedIds.map(ch => [ch.id, ch.organisation_name]));

        return dashboardData.requestedIds.map(({ id }, index) => {
            const records = dashboardData.data[id] || [];
            const last30DaysRecords = records.filter(r => isWithinDays(r.rowKey, 30, now));
            
            const totalAppearances = last30DaysRecords.length;
            const top5Count = last30DaysRecords.filter(r => r.search_position <= 5).length;
            const top5Percent = totalAppearances > 0 ? (top5Count / totalAppearances) * 100 : 0;
            
            const avgPosition = totalAppearances > 0 
                ? last30DaysRecords.reduce((sum, r) => sum + r.search_position, 0) / totalAppearances 
                : 0;

            return {
                id,
                name: careHomeNames.get(id) || id,
                avgPosition: Math.round(avgPosition * 10) / 10, // Round to 1 decimal
                totalAppearances,
                top5Count,
                top5Percent: Math.round(top5Percent * 10) / 10,
                color: CHART_COLORS[index % CHART_COLORS.length]
            };
        });
    }, [dashboardData]);

    // Calculate overall average position across all care homes (for comparison)
    const overallAvgPosition = useMemo((): number => {
        if (careHomeRankingStats.length === 0) return 0;
        const totalRecords = careHomeRankingStats.reduce((sum, ch) => sum + ch.totalAppearances, 0);
        if (totalRecords === 0) return 0;
        
        const weightedSum = careHomeRankingStats.reduce(
            (sum, ch) => sum + (ch.avgPosition * ch.totalAppearances), 0
        );
        return Math.round((weightedSum / totalRecords) * 10) / 10;
    }, [careHomeRankingStats]);

    // Process data for line chart (searches per month per care home)
    const lineChartData = useMemo((): MonthlySearchData[] => {
        if (!dashboardData) return [];

        const monthlyData: Record<string, Record<string, number>> = {};
        
        // Get the last 12 months
        const now = new Date();
        for (let i = 11; i >= 0; i--) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            monthlyData[monthKey] = {};
            
            // Initialize all care homes with 0 for each month
            dashboardData.requestedIds.forEach(({ id }) => {
                monthlyData[monthKey][id] = 0;
            });
        }

        // Count searches per care home per month
        Object.entries(dashboardData.data).forEach(([careHomeId, records]) => {
            records.forEach(record => {
                const date = new Date(record.rowKey.substring(0, 23)); // ISO date portion
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                
                if (monthlyData[monthKey]) {
                    monthlyData[monthKey][careHomeId] = (monthlyData[monthKey][careHomeId] || 0) + 1;
                }
            });
        });

        // Convert to chart format
        return Object.entries(monthlyData)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, counts]) => ({
                month: formatMonth(month),
                ...counts
            }));
    }, [dashboardData]);

    // Process data for stacked bar chart (top 5 positions vs remaining)
    const stackedBarData = useMemo((): StackedBarData[] => {
        if (!dashboardData) return [];

        const monthlyData: Record<string, Record<string, { top5: number; remaining: number }>> = {};
        
        // Get the last 12 months
        const now = new Date();
        for (let i = 11; i >= 0; i--) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            monthlyData[monthKey] = {};
            
            // Initialize all care homes
            dashboardData.requestedIds.forEach(({ id }) => {
                monthlyData[monthKey][id] = { top5: 0, remaining: 0 };
            });
        }

        // Count top 5 vs remaining positions per care home per month
        Object.entries(dashboardData.data).forEach(([careHomeId, records]) => {
            records.forEach(record => {
                const date = new Date(record.rowKey.substring(0, 23));
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                
                if (monthlyData[monthKey] && monthlyData[monthKey][careHomeId]) {
                    if (record.search_position <= 5) {
                        monthlyData[monthKey][careHomeId].top5++;
                    } else {
                        monthlyData[monthKey][careHomeId].remaining++;
                    }
                }
            });
        });

        // Convert to chart format (flattened for grouped bar chart)
        const result: StackedBarData[] = [];
        const careHomeNames = new Map(dashboardData.requestedIds.map(ch => [ch.id, ch.organisation_name]));
        
        Object.entries(monthlyData)
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([month, careHomes]) => {
                Object.entries(careHomes).forEach(([careHomeId, counts]) => {
                    result.push({
                        month: formatMonth(month),
                        careHomeId,
                        careHomeName: careHomeNames.get(careHomeId) || careHomeId,
                        top5: counts.top5,
                        remaining: counts.remaining
                    });
                });
            });

        return result;
    }, [dashboardData]);

    // Line chart options
    const lineChartOptions = useMemo((): AgChartOptions => {
        if (!dashboardData) return { data: [] };

        const careHomeNames = new Map(dashboardData.requestedIds.map(ch => [ch.id, ch.organisation_name]));

        return {
            title: {
                text: 'Monthly Search Appearances',
            },
            subtitle: {
                text: 'Number of times each care home appeared in search results'
            },
            data: lineChartData,
            series: dashboardData.requestedIds.map(({ id }, index) => ({
                type: 'line' as const,
                xKey: 'month',
                yKey: id,
                yName: careHomeNames.get(id) || id,
                stroke: CHART_COLORS[index % CHART_COLORS.length],
                marker: {
                    fill: CHART_COLORS[index % CHART_COLORS.length],
                    stroke: CHART_COLORS[index % CHART_COLORS.length],
                }
            })),
            legend: {
                position: 'bottom'
            }
        };
    }, [dashboardData, lineChartData]);

    // Stacked bar chart options
    const stackedBarOptions = useMemo((): AgChartOptions => {
        if (!dashboardData || stackedBarData.length === 0) return { data: [] };

        // Group data by month for stacked bar
        const months = [...new Set(stackedBarData.map(d => d.month))];
        const careHomeIds = dashboardData.requestedIds.map(ch => ch.id);
        const careHomeNames = new Map(dashboardData.requestedIds.map(ch => [ch.id, ch.organisation_name]));

        // Transform data for grouped stacked bars
        const groupedData = months.map(month => {
            const monthData: Record<string, number | string> = { month };
            careHomeIds.forEach(id => {
                const record = stackedBarData.find(d => d.month === month && d.careHomeId === id);
                monthData[`${id}_top5`] = record?.top5 || 0;
                monthData[`${id}_remaining`] = record?.remaining || 0;
            });
            return monthData;
        });

        // Create series for each care home (top5 and remaining)
        const barSeries = careHomeIds.flatMap((id, index) => {
            const color = CHART_COLORS[index % CHART_COLORS.length];
            const name = careHomeNames.get(id) || id;
            
            return [
                {
                    type: 'bar' as const,
                    xKey: 'month',
                    yKey: `${id}_top5`,
                    yName: `${name} (Top 5)`,
                    fill: color,
                    stacked: true,
                    stackGroup: id
                },
                {
                    type: 'bar' as const,
                    xKey: 'month',
                    yKey: `${id}_remaining`,
                    yName: `${name} (Position 6+)`,
                    fill: color,
                    fillOpacity: 0.4,
                    stacked: true,
                    stackGroup: id
                }
            ];
        });

        return {
            title: {
                text: 'Search Position Distribution'
            },
            subtitle: {
                text: 'Top 5 positions vs remaining positions per care home'
            },
            data: groupedData,
            series: barSeries,
            legend: {
                position: 'bottom'
            }
        };
    }, [dashboardData, stackedBarData]);

    // Helper function to format month
    function formatMonth(monthKey: string): string {
        const [year, month] = monthKey.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
    }

    if (loading) {
        return (
            <div className="dashboard-container">
                <div className="dashboard-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboard data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-container">
                <div className="dashboard-error">
                    <h2>Dashboard Error</h2>
                    <p>{error}</p>
                    <Link to="/" className="back-link">← Back to Search</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <header className="dashboard-header">
                <div className="header-content">
                    <Link to="/" className="back-link">← Back to Search</Link>
                    <h1>Care Home Performance Dashboard</h1>
                    <p className="dashboard-subtitle">
                        Viewing data for {dashboardData?.requestedIds.length} care home{dashboardData?.requestedIds.length !== 1 ? 's' : ''} 
                        {' '}• {dashboardData?.meta.totalRecords.toLocaleString()} total search records
                    </p>
                </div>
            </header>

            {/* Total Appearances Score Cards */}
            {totalAppearancesStats && (
                <section className="score-cards-section">
                    <h2 className="section-title">Total Appearances</h2>
                    <div className="score-cards-grid">
                        <div className="score-card">
                            <div className="score-card-header">
                                <span className="score-card-period">Last 7 Days</span>
                            </div>
                            <div className="score-card-value">
                                {totalAppearancesStats.last7Days.current.toLocaleString()}
                            </div>
                            <div className={`score-card-change ${totalAppearancesStats.last7Days.change >= 0 ? 'positive' : 'negative'}`}>
                                <span className="change-arrow">{totalAppearancesStats.last7Days.change >= 0 ? '↑' : '↓'}</span>
                                <span className="change-value">{Math.abs(totalAppearancesStats.last7Days.change).toLocaleString()}</span>
                                <span className="change-percent">({totalAppearancesStats.last7Days.changePercent >= 0 ? '+' : ''}{totalAppearancesStats.last7Days.changePercent.toFixed(1)}%)</span>
                            </div>
                            <div className="score-card-comparison">vs previous 7 days ({totalAppearancesStats.last7Days.previous.toLocaleString()})</div>
                        </div>

                        <div className="score-card">
                            <div className="score-card-header">
                                <span className="score-card-period">Last 30 Days</span>
                            </div>
                            <div className="score-card-value">
                                {totalAppearancesStats.last30Days.current.toLocaleString()}
                            </div>
                            <div className={`score-card-change ${totalAppearancesStats.last30Days.change >= 0 ? 'positive' : 'negative'}`}>
                                <span className="change-arrow">{totalAppearancesStats.last30Days.change >= 0 ? '↑' : '↓'}</span>
                                <span className="change-value">{Math.abs(totalAppearancesStats.last30Days.change).toLocaleString()}</span>
                                <span className="change-percent">({totalAppearancesStats.last30Days.changePercent >= 0 ? '+' : ''}{totalAppearancesStats.last30Days.changePercent.toFixed(1)}%)</span>
                            </div>
                            <div className="score-card-comparison">vs previous 30 days ({totalAppearancesStats.last30Days.previous.toLocaleString()})</div>
                        </div>

                        <div className="score-card">
                            <div className="score-card-header">
                                <span className="score-card-period">Last 90 Days</span>
                            </div>
                            <div className="score-card-value">
                                {totalAppearancesStats.last90Days.current.toLocaleString()}
                            </div>
                            <div className={`score-card-change ${totalAppearancesStats.last90Days.change >= 0 ? 'positive' : 'negative'}`}>
                                <span className="change-arrow">{totalAppearancesStats.last90Days.change >= 0 ? '↑' : '↓'}</span>
                                <span className="change-value">{Math.abs(totalAppearancesStats.last90Days.change).toLocaleString()}</span>
                                <span className="change-percent">({totalAppearancesStats.last90Days.changePercent >= 0 ? '+' : ''}{totalAppearancesStats.last90Days.changePercent.toFixed(1)}%)</span>
                            </div>
                            <div className="score-card-comparison">vs previous 90 days ({totalAppearancesStats.last90Days.previous.toLocaleString()})</div>
                        </div>
                    </div>
                </section>
            )}

            {/* Average Ranking Cards */}
            {careHomeRankingStats.length > 0 && (
                <section className="ranking-section">
                    <div className="ranking-header">
                        <h2 className="section-title">Average Ranking (Last 30 Days)</h2>
                        <div className="overall-avg">
                            Overall Avg: <strong>{overallAvgPosition.toFixed(1)}</strong>
                        </div>
                    </div>
                    <div className="ranking-cards-grid">
                        {careHomeRankingStats.map(stats => (
                            <div 
                                key={stats.id} 
                                className="ranking-card"
                                style={{ borderTopColor: stats.color }}
                            >
                                <div className="ranking-card-name">{stats.name}</div>
                                <div className="ranking-card-metrics">
                                    <div className="ranking-metric primary">
                                        <span className="metric-value">{stats.avgPosition.toFixed(1)}</span>
                                        <span className="metric-label">Avg Position</span>
                                        <span className={`metric-comparison ${stats.avgPosition <= overallAvgPosition ? 'better' : 'worse'}`}>
                                            {stats.avgPosition <= overallAvgPosition ? '▲ Better' : '▼ Below'} avg
                                        </span>
                                    </div>
                                    <div className="ranking-metric">
                                        <span className="metric-value">{stats.totalAppearances.toLocaleString()}</span>
                                        <span className="metric-label">Appearances</span>
                                    </div>
                                    <div className="ranking-metric">
                                        <span className="metric-value">{stats.top5Percent.toFixed(0)}%</span>
                                        <span className="metric-label">In Top 5</span>
                                        <span className="metric-detail">{stats.top5Count} of {stats.totalAppearances}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <div className="dashboard-summary">
                {dashboardData?.requestedIds.map((careHome, index) => (
                    <div 
                        key={careHome.id} 
                        className="care-home-badge"
                        style={{ borderLeftColor: CHART_COLORS[index % CHART_COLORS.length] }}
                    >
                        <span className="badge-name">{careHome.organisation_name}</span>
                        <span className="badge-id">{careHome.id}</span>
                    </div>
                ))}
            </div>

            <div className="charts-grid">
                <div className="chart-card">
                    <AgCharts options={lineChartOptions} />
                </div>
                
                <div className="chart-card">
                    <AgCharts options={stackedBarOptions} />
                </div>
            </div>

            <footer className="dashboard-footer">
                <p>Data covers the last {dashboardData?.meta.periodMonths} months • Query completed in {dashboardData?.meta.queryDurationMs}ms</p>
            </footer>
        </div>
    );
};

export default CPDashboard;
